import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {getLocalDateFromServer, formatDateTime, formatDateTimeWithSeconds} from '@/shared/helpers/dates';
import {useUserStore} from "@/console-new/stores/user.store";
import {useLanguageStore, useScriptStore} from "@/shared/stores";
import {useFunnelStore} from "@/console-new/stores/funnel.store";

export const useAdministrationStore = defineStore('administration', {
	state: () => ({
		businessTransfers: null,
		businessTransfersTotal: null,
		businessTransactions: null,
		businessTransactionsTotal: null,
		businessEpsonReceipts: null,
		businessEpsonReceiptsTotal: null,
		businessApiscontrinoReceipts: null,
		businessApiscontrinoReceiptsTotal: null,
		businessInvoices: null,
		businessToCustomerInvoices: {invoice: [], credit_note: []},
		businessToCustomerInvoicesTotal: {invoice: 0, credit_note: 0},
		selfInvoices: null,
		selfInvoicesTotal: null,
		businessInvoicesTotal: null,
		businessBalanceChanges: null,
		businessBalanceChangesTotal: null,
		businessCreditTopups: null,
		businessCreditTopupsTotal: null,
		activeTransfer: null,
		showStripeBottomSheet: false,
		stripeConnectInstance: null,
		stripeConnectInstanceBusinessId: null,
	}),
	getters: {
		b2cInvoicesIds: state => {
			let finalIds = {};
			Object.values(state.businessToCustomerInvoices).forEach(invoices => {
				invoices.forEach(t => {
					finalIds[t.invoice_id] = t;
				});
			});
			return finalIds;
		}
	},
	actions: {
		resetData() {
			this.businessTransfers = null;
			this.businessTransfersTotal = null;
			this.businessTransactions = null;
			this.businessTransactionsTotal = null;
			this.businessInvoices = null;
			this.businessBusinessToCustomerInvoices = null;
			this.businessInvoicesTotal = null;
			this.businessBalanceChanges = null;
			this.businessBalanceChangesTotal = null;
			this.businessCreditTopups = null;
			this.businessCreditTopupsTotal = null;
			this.businessBusinessToCustomerInvoicesTotal = null;
			this.activeTransfer = null;
		},
		getReconcileTransactionsTransfer(callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId || !this.activeTransfer) { callback && callback(false); return; }

			API.init().getReconcileTransactionsTransfer(userStore.currBusinessId, this.activeTransfer.transfer_id, r=> {
				if(r && r.result === "OK" && r.transactions) {
					if(callback) { callback(r); }
				} else {
					if(callback) { callback(false); }
				}
			});
		},
		getFilteredStripeBalance(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredStripeBalance(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessTransfers = [];
				this.businessTransfersTotal = 0;
				if(r && r.result === "OK") {
					if(r.transfers && r.transfers.selected && r.transfers.selected.length) {
						r.transfers.selected.forEach(t => {
							this.businessTransfers.push(this.normalizeTransfer(t));
						});
						this.businessTransfersTotal = Number(r.transfers.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredInvoices(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredInvoices(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessInvoices = [];
				this.businessInvoicesTotal = 0;
				if(r && r.result === "OK") {
					if(r.invoices && r.invoices.selected && r.invoices.selected.length) {
						r.invoices.selected.forEach(t => {
							this.businessInvoices.push(this.normalizeInvoice(t));
						});
						this.businessInvoicesTotal = Number(r.invoices.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredEpsonReceipts(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredEpsonReceipts(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessEpsonReceipts = [];
				this.businessEpsonReceiptsTotal = 0;
				if(r && r.result === "OK") {
					if(r.receipts && r.receipts.selected && r.receipts.selected.length) {
						r.receipts.selected.forEach(t => {
							if(r.receipts_map && r.receipts_map[t.business_receipt_epson_id]) {
								t.order_id = r.receipts_map[t.business_receipt_epson_id]['order_id'];
								t.type = r.receipts_map[t.business_receipt_epson_id]['type'];
							} else {
								t.order_id = null;
								t.type = null;
							}
							this.businessEpsonReceipts.push(this.normalizeEpsonReceipt(t));
						});
						this.businessEpsonReceiptsTotal = Number(r.receipts.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredApiscontrinoReceipts(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredApiscontrinoReceipts(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessApiscontrinoReceipts = [];
				this.businessApiscontrinoReceiptsTotal = 0;
				if(r && r.result === "OK") {
					if(r.receipts && r.receipts.selected && r.receipts.selected.length) {
						r.receipts.selected.forEach(t => {
							if(r.receipts_map && r.receipts_map[t.business_receipt_id]) {
								t.order_id = r.receipts_map[t.business_receipt_id]['order_id'];
								t.type = r.receipts_map[t.business_receipt_id]['type'];
							} else {
								t.order_id = null;
								t.type = null;
							}
							this.businessApiscontrinoReceipts.push(this.normalizeApiscontrinoReceipt(t));
						});
						this.businessApiscontrinoReceiptsTotal = Number(r.receipts.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredBusinessToCustomerInvoices(filters, callback = null, documentType = 'invoice') {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			let typedFilters = Object.assign({}, filters || {}, {document_type: [documentType]});
			API.init().getFilteredBusinessToCustomerInvoices(userStore.currBusinessId, JSON.stringify(typedFilters), r=> {
				this.businessToCustomerInvoices[documentType] = [];
				this.businessToCustomerInvoicesTotal[documentType] = 0;
				if(r && r.result === "OK") {
					if(r.invoices && r.invoices.selected && r.invoices.selected.length) {
						r.invoices.selected.forEach(t => {
							this.businessToCustomerInvoices[documentType].push(this.normalizeBusinessToCustomerInvoice(t));
						});
						this.businessToCustomerInvoicesTotal[documentType] = Number(r.invoices.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredSelfInvoices(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredSelfInvoices(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.selfInvoices = [];
				this.selfInvoicesTotal = 0;
				if(r && r.result === "OK") {
					if(r.self_invoices && r.self_invoices.selected && r.self_invoices.selected.length) {
						r.self_invoices.selected.forEach(t => {
							this.selfInvoices.push(this.normalizeSelfInvoice(t));
						});
						this.selfInvoicesTotal = Number(r.self_invoices.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredStripeTransactions(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredStripeTransactions(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessTransactions = [];
				this.businessTransactionsTotal = 0;
				if(r && r.result === "OK") {
					if(r.transactions && r.transactions.selected && r.transactions.selected.length) {
						r.transactions.selected.forEach(t => {
							this.businessTransactions.push(this.normalizeTransaction(t));
						});
						this.businessTransactionsTotal = Number(r.transactions.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredBalanceChanges(filters, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getFilteredBalanceChanges(userStore.currBusinessId, JSON.stringify(filters || []), r=> {
				this.businessBalanceChanges = [];
				this.businessBalanceChangesTotal = 0;
				if(r && r.result === "OK") {
					if(r.changes && r.changes.selected && r.changes.selected.length) {
						r.changes.selected.forEach(b => {
							this.businessBalanceChanges.push(this.normalizeBalanceChange(b));
						});
						this.businessBalanceChangesTotal = Number(r.changes.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getFilteredCreditTopups(filters, callback = null) {
			let userStore = useUserStore();
			if (!userStore.currBusinessId) { callback && callback([]); return; }

			API.init().getBusinessCreditTopups(userStore.currBusinessId, JSON.stringify(filters || []), r => {
				this.businessCreditTopups = [];
				this.businessCreditTopupsTotal = 0;
				if (r && r.result === "OK") {
					if (r.topups && r.topups.selected && r.topups.selected.length) {
						r.topups.selected.forEach(t => {
							this.businessCreditTopups.push(this.normalizeCreditTopup(t));
						});
						this.businessCreditTopupsTotal = Number(r.topups.selected_num || 0);
					}
					if (callback) { callback(true); }
				} else {
					if (callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getCurrentShiftTransactions(todayShiftStart, todayShiftEnd, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback([]); return; }

			let formattedStartDate = formatDateTimeWithSeconds(todayShiftStart);
			let formattedEndDate = formatDateTimeWithSeconds(todayShiftEnd);
			let currentOffset = 0;
			let pageSize = 10000;
			let finalAmount = 0;
			let finalTransactions = 0;
			let finalRefunds = 0;
			let finalCancels = 0;
			let finalFees = 0;
			let fetchFn = ()=>{
				let filters = {
					"offset": currentOffset,
					"page_size": pageSize,
					"date": ">" + formattedStartDate,
					"type": ["connect_order_pos", 'pos_order'],
					"transactions.date": "<" + formattedEndDate
				};
				API.init().getFilteredStripeTransactions(userStore.currBusinessId, JSON.stringify(filters), r=> {
					if(r && r.result === "OK" && r.transactions && r.transactions.selected && r.transactions.selected_num) {
						if(r.transactions.selected.length) {
							r.transactions.selected.forEach(t => {
								if(t.status !== 'canceled') {
									finalAmount += Number(t.amount);
									finalFees += Number(t.application_fee_amount);
									if(t.refunded_amount) {
										finalAmount -= Number(t.refunded_amount);
										finalRefunds += 1;
									} else {
										finalTransactions += 1;
									}
								} else {
									finalCancels += 1;
								}
							});

						}

						if(Number(r.transactions.selected_num) > pageSize + currentOffset) {
							currentOffset += pageSize;
							fetchFn();
						} else {
							if(callback) {
								callback({
									amount: finalAmount,
									fees: finalFees,
									transactions: finalTransactions,
									refunds: finalRefunds,
									cancels: finalCancels
								});
							}

						}
					} else {
						if(callback) { callback(false, r ? r.details : null); }
					}
				});
			}
			fetchFn();


		},
		refundStripeTransaction(transactionId, amount, reason, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) { callback && callback(false); }
			API.init().refundStripeTransaction(userStore.currBusinessId, transactionId, amount, reason, r => {
				if(r && r.result === "OK") {
					if(callback) { callback(true); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		getStripeDashboardLink(callback = null) {
			let userStore = useUserStore();
			API.init().getStripeExpressDashboardUrl(userStore.currBusinessId, r => {
				if (r && r.result === "OK" && r.url) {
					if(callback) { callback(r.url); }
				} else {
					if(callback) { callback(false, r ? r.details : null); }
				}
			});
		},
		connectStripe(callback) {
			let userStore = useUserStore();

			API.init().connectStripe(userStore.currBusinessId, r => {
				if (r.result === "OK" && r.account) {
					userStore.currBusiness.stripe_account = r.account;
					if (callback) {
						callback(r.account);
					}
				} else {
					if (callback) {
						callback(false, r ? r.details : null);
					}
				}
			});
		},
		migrateStripeToCustom(callback) {
			let userStore = useUserStore();

			API.init().migrateStripeToCustom(userStore.currBusinessId, r => {
				if (r.result === "OK" && r.account) {
					userStore.currBusiness.stripe_account = r.account;
					userStore.currBusiness.stripe_account_type = "custom";
					userStore.currBusiness.config_payments = 0;
					if (callback) {
						callback(r.account);
					}
				} else {
					if (callback) {
						callback(false, r ? r.details : null);
					}
				}
			});
		},
		getStripeConnectSession(sessionType, callback) {
			let userStore = useUserStore();
			API.init().getStripeConnectSession(userStore.currBusinessId, sessionType, r => {
				if (r.result === "OK" && r.client_secret) {
					if (callback) {
						callback(r.client_secret);
					}
				} else {
					if (callback) {
						callback(false, r ? r.details : null);
					}
				}
			});
		},
		postLoadStripeConnectScript(sessionType, callback) {
			const fetchClientSecret = async () => {
				return new Promise((resolve, reject) => {
					this.getStripeConnectSession(sessionType, (secret, details) => {
						if (secret) {
							resolve(secret);
						} else {
							callback(false, details);
							reject(new Error("Failed to fetch client secret"+(details ? ": "+details : "")));
						}
					});
				});
			}
			const getCssColor = (variable) => {
				const element = document.documentElement;
				const computedStyle = getComputedStyle(element);
				return computedStyle.getPropertyValue(variable).trim();
			};

			const stripeConnectInstance = StripeConnect.init({
				publishableKey: STRIPE_PUBLIC_KEY_2,
				fetchClientSecret: fetchClientSecret,
				appearance: {
					variables: {
						fontFamily: '"Montserrat", sans-serif',
						colorBackground: getCssColor("--color-bg-primary-1"),
						colorPrimary: getCssColor("--color-primary"),
						colorDanger: getCssColor("--color-danger"),
						borderRadius: "10px",
						spacingUnit: "10px",
					}
				},
				fonts: [
					{
						cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap',
					}
				]
			});

			this.stripeConnectInstance = stripeConnectInstance;
			this.stripeConnectInstanceBusinessId = useUserStore().currBusinessId;

			callback(stripeConnectInstance);
		},
		getStripeConnectInstance(sessionType, callback) {

			const postStripeAccountFun = () => {

				const userStore = useUserStore();
				if(!this.stripeConnectInstance) {
					window.StripeConnect = window.StripeConnect || {};
					StripeConnect.onLoad = () => {
						this.postLoadStripeConnectScript(sessionType, callback)
					};
					const scriptStore = useScriptStore();
					scriptStore.loadScript("stripeConnect", () => {});
				} else if(this.stripeConnectInstanceBusinessId !== userStore?.currBusinessId) {
					// Reset the instance when switching businesses
					this.stripeConnectInstance = null;
					this.stripeConnectInstanceBusinessId = null;
					
					// Check if StripeConnect is already loaded
					if (window.StripeConnect && window.StripeConnect.init) {
						this.postLoadStripeConnectScript(sessionType, callback)
					} else {
						callback(false, "StripeConnect script not loaded");
					}
				} else {
					callback(this.stripeConnectInstance);
				}

			}

			const userStore = useUserStore();
			if(userStore.currBusiness && userStore.currBusiness.stripe_account) {
				postStripeAccountFun();
			} else {
				this.connectStripe((account, details) => {

					if(account) {
						postStripeAccountFun();
					} else {
						callback(false, details);
					}
				});
			}

		},
		normalizeTransfer(transfer) {
			transfer.date = transfer.date ? getLocalDateFromServer(transfer.date) : null;
			transfer.amount = Number(transfer.amount || 0);
			transfer.transfer_id = Number(transfer.transfer_id || 0);
			transfer.business_id = Number(transfer.business_id || 0);
			return transfer;
		},
		normalizeInvoice(invoice) {
			invoice.date = invoice.date ? getLocalDateFromServer(invoice.date) : null;
			invoice.amount = Number(invoice.amount || 0);
			return invoice;
		},
		normalizeEpsonReceipt(receipt) {
			receipt.date = receipt.date ? getLocalDateFromServer(receipt.date) : null;
			receipt.total = Number(receipt.total || 0);
			receipt.receipt_number = Number(receipt.receipt_number || 0);
			receipt.zrep = Number(receipt.zrep || 0);
			receipt.epsonNumber = receipt.zrep.toString().padStart(4, '0')+"-"+receipt.receipt_number.toString().padStart(4, '0');
			receipt.status = "emitted";
			receipt.refunded = Number(receipt.refunded || 0);
			if(receipt.refunded === 1) {
				receipt.status = "refunded";
			} else if(receipt.refunded === 2) {
				receipt.status = "canceled";
			}
			return receipt;
		},
		normalizeApiscontrinoReceipt(receipt) {
			receipt.date = receipt.date ? getLocalDateFromServer(receipt.date) : null;
			receipt.total = Number(receipt.total || 0);
			receipt.ade_id = receipt.ade_id.split("/")[1];
			return receipt;
		},
		normalizeBusinessToCustomerInvoice(invoice) {
			let languageStore = useLanguageStore();
			invoice.date = invoice.date ? getLocalDateFromServer(invoice.date) : null;
			invoice.amount = Number(invoice.amount || 0);
			invoice.address_id = Number(invoice.address_id || 0);
			invoice.reference_invoice_id = Number(invoice.reference_invoice_id || 0);
			invoice.apifattura_invoice_id = Number(invoice.apifattura_invoice_id || 0);
			invoice.business_id = Number(invoice.business_id || 0);
			invoice.customer_id = Number(invoice.customer_id || 0);
			invoice.invoice_id = Number(invoice.invoice_id || 0);
			invoice.state_readable = languageStore.getString("state_"+invoice.state);
			if(invoice.reference_invoice_id && ['failed', 'electronic', 'confirmed'].includes(invoice.state)) {
				switch (invoice.state) {
					case 'failed': 		invoice.state_readable = languageStore.getString("state_refund_failed");	break;
					case 'electronic':	invoice.state_readable = languageStore.getString("credit_note_requested");	break;
					case 'confirmed':	invoice.state_readable = languageStore.getString("credit_note_issued");	break;
				}
			}
			if(invoice.address) {
				invoice.address.lat = invoice.address.lat ? Number(invoice.address.lat) : null;
				invoice.address.lng = invoice.address.lng ? Number(invoice.address.lng) : null;
			}
			invoice.error_readable = invoice.error ? languageStore.getString(invoice.error) : '-';
			return invoice;
		},
		normalizeSelfInvoice(selfInvoice) {
			selfInvoice.self_invoice_id = Number(selfInvoice.self_invoice_id || 0);
			selfInvoice.date = selfInvoice.date ? getLocalDateFromServer(selfInvoice.date) : null;
			selfInvoice.amount = Number(selfInvoice.amount || 0);
			return selfInvoice;
		},
		normalizeTransaction(transaction) {
			transaction.transaction_id = Number(transaction.transaction_id || 0);
			transaction.business_id = Number(transaction.business_id || 0);
			transaction.last_charge_id = transaction.last_charge_id || null;
			transaction.order_id = transaction.order_id || null;
			transaction.date = transaction.date ? getLocalDateFromServer(transaction.date) : null;
			transaction.amount = Number(transaction.amount || 0);
			transaction.application_fee_amount = Number(transaction.application_fee_amount || 0);
			transaction.type = transaction.type || null;
			transaction.refunded_amount = Number(transaction.refunded_amount || 0);
			transaction.refund_reason = transaction.refund_reason || null;
			return transaction;
		},
		normalizeBalanceChange(b) {
			b.amount = Number(b.amount || 0);
			b.balance_pre_change = Number(b.balance_pre_change || 0);
			b.reason = b.reason || "";
			b.date = b.date ? getLocalDateFromServer(b.date) : null;
			return b;
		},
		normalizeCreditTopup(t) {
			t.amount = Number(t.amount || 0);
			t.date = t.date ? getLocalDateFromServer(t.date) : null;
			t.customer_name = t.customer_name || '-';
			t.customer_type = t.customer_type || null;
			t.customer_person_id = t.customer_person_id ? Number(t.customer_person_id) : null;
			t.customer_company_id = t.customer_company_id ? Number(t.customer_company_id) : null;
			t.type = t.type || null;
			return t;
		},
	},
});